// ═══════════════════════════════════════════════════════════════════
// MenuBackdrop.js — THE BACKDROP BAND (the Empire's one menu, React face)
// MASTER COPY: gamemaster/engine/MenuBackdrop.js — vendored byte-identical.
// Rides with ieBackdropCore.js (the brain), ieBackdrop.js (hooks),
// AemiliaGuide.js (she lives here), markData.js (fonts), ieSocial.js.
// ───────────────────────────────────────────────────────────────────
// caP'n, 2026-10-21: "alllll of that non essential screen clutter should be
// housed in there in every game, world chat all of it … world chat should
// appear in the backdrop like the log" · "combine it now with Terror's
// backdrop … we need continuity".
//
// ⭐ THE SHAPE IS TERROR'S BAND (railDock + RoomOverlay + RoomBackdrop) and
// Sweet Dreams' (chrome.js): a painted strip down the RIGHT side that is
// always there on a computer, and the game leaves room for it.
//
//   ┌──────────────┐  ☰ MENU          — opens the MENU panel (rows: Play ·
//   │ — ✉  ♥  ♢    │  the tab row        Settings · Log out at the foot)
//   │              │                   — the fold "—" folds the whole band
//   │  one panel   │                     into one draggable ☰ MENU pill
//   │  at a time   │                   — tabs: the LOG (world chat), Friends,
//   │              │                     Party… ONE panel open at a time;
//   │   Aemilia    │                     tap the open one to close it
//   └──────────────┘  her nook          — Aemilia's house, at the foot
//
// Phone / portrait: no band (as in Terror and Sweet Dreams) — a ☰ MENU pill
// (bottom-left, thumb reach) opens the same contents as a sheet.
//
// A title says: its pill corner on touch (`pillTouch`), its `storageKey`, its
// resting panel (`defaultPanel`), its painting (`art` URL, or `Art` component),
// and — through ieBackdrop.js — its rows, chips and tabs. A tab's owner draws
// its own body into railPanelStyle() while its tab is open; portal it to
// document.body (BandPortal), because this layer paints over the title's root.
//
// ⚠ IT PAINTS ABOVE THE TITLE (a sibling of <App/>; a title's root is its own
// stacking context). The title must LEAVE ROOM for the band: bandWidthPx()
// while useBand().active && !collapsed (Sweet Dreams' viewW(), Terror's
// RECORD_RESERVE()).
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MK, injectMarkChrome } from "./markData";
import { useSocialHidden } from "./ieSocial";
import { band, BAND, bandWidthPx } from "./ieBackdropCore";
import { useBand, openBackdrop, closeBackdrop } from "./ieBackdrop";
import { AemiliaSprite, useAemiliaMode, useAemiliaOpen, useAemiliaUnseen, openAemilia } from "./AemiliaGuide";
import { iconNode } from "./IEGlyph";   // "ie:heart" / "ie:eyes" icons — never the ✦ (caP'n, 2026-10-21)

// The Empire's chrome palette (Aemilia's twilight family). `accent` is the one knob.
const B = {
  panel: "rgba(10,8,28,0.96)",
  glass: "rgba(10,8,28,0.82)",
  row: "rgba(255,255,255,0.045)",
  rowHi: "rgba(255,255,255,0.10)",
  edge: "#3a3170",
  text: "#ece8ff",
  dim: "#bdb6e0",          // 8.9:1 on the panel
  ink: "#1b1440",
  star: "#ffd88a",
};

const SECTION_ORDER = ["play", "people", "settings"];
const SECTION_LABEL = { play: "Play", people: "People", settings: "Settings" };
const NOOK_H = 118;                         // Aemilia's house at the foot of the band
const Z = { art: 40, panel: 64, chrome: 72, pill: 75, sheet: 250 };

function injectKeys() {
  if (typeof document === "undefined" || document.getElementById("ieb-keys-v2")) return;
  const st = document.createElement("style");
  st.id = "ieb-keys-v2";
  st.textContent = `
@keyframes iebIn { from { transform: translateX(28px); opacity: 0 } to { transform: none; opacity: 1 } }
@keyframes iebFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes iebBob { 0% { transform: translateY(0) } 100% { transform: translateY(-6px) } }
@keyframes iebDrift { 0% { transform: translate(0,0) rotate(0deg) } 100% { transform: translate(-10px,8px) rotate(8deg) } }
@keyframes iebRim { 0%,100% { opacity: .75 } 50% { opacity: 1 } }
.ieb-row:hover, .ieb-row:focus-visible { background: ${B.rowHi} !important; border-color: var(--ieb-accent) !important; outline: none }
.ieb-btn:focus-visible { outline: 2px solid ${B.star}; outline-offset: 2px }
@media (prefers-reduced-motion: reduce) { .ieb-anim { animation: none !important } }`;
  document.head.appendChild(st);
}
// ── THE PAINTING (placeholder) ──────────────────────────────────────
function BackdropArt({ accent }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 340 900" preserveAspectRatio="xMidYMax slice"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
      <defs>
        <linearGradient id="iebSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1040" />
          <stop offset="45%" stopColor="#0e0a26" />
          <stop offset="100%" stopColor="#06040f" />
        </linearGradient>
        <radialGradient id="iebHole" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000" />
          <stop offset="62%" stopColor="#0a0418" />
          <stop offset="78%" stopColor="#7c3aed" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="iebSea" cx="50%" cy="0%" r="80%">
          <stop offset="0%" stopColor="#2a1d6e" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#2a1d6e" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="340" height="900" fill="url(#iebSky)" />
      {/* stars */}
      {[[30,40],[80,120],[300,60],[250,180],[40,260],[190,30],[320,300],[120,340],[60,520],[280,560],[210,700],[20,780]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r={i % 3 ? 1 : 1.6} fill="#e7dcff" opacity={0.35 + (i % 4) * 0.12} />
      ))}
      {/* the cracked eclipse, low — behind Aemilia's nook at the foot */}
      <g className="ieb-anim" style={{ animation: "iebRim 6s ease-in-out infinite" }}>
        <circle cx="206" cy="836" r="58" fill="none" stroke={accent} strokeWidth="5" opacity="0.85" />
        <circle cx="206" cy="836" r="54" fill="#07041a" />
        <path d="M178 794 L196 818 L186 836 L204 854 L198 880" fill="none" stroke={accent} strokeWidth="2" opacity="0.7" />
        <path d="M234 796 L222 818 L236 830" fill="none" stroke={accent} strokeWidth="1.6" opacity="0.55" />
      </g>
      {/* a violet hole low on the panel */}
      <circle cx="70" cy="420" r="90" fill="url(#iebHole)" opacity="0.8" />
      {/* drifting shards */}
      <g className="ieb-anim" style={{ animation: "iebDrift 14s ease-in-out infinite alternate", transformOrigin: "60px 210px" }}>
        <polygon points="46,190 78,200 70,232 40,222" fill="#5b8fd9" opacity="0.32" />
      </g>
      <g className="ieb-anim" style={{ animation: "iebDrift 18s ease-in-out infinite alternate-reverse", transformOrigin: "290px 790px" }}>
        <polygon points="278,770 308,780 302,812 274,804" fill="#8a6cf0" opacity="0.28" />
      </g>
      {/* a sea of Anima, high */}
      <rect y="0" width="340" height="180" fill="url(#iebSea)" />
    </svg>
  );
}

// ── The rows: the MENU panel's contents (band and sheet share it) ──
function RowList({ rows, chips, accent, onRun, foot = true }) {
  const bySection = {};
  rows.forEach((it) => { const k = it.section || "settings"; (bySection[k] = bySection[k] || []).push(it); });
  Object.values(bySection).forEach((l) => l.sort((a, b) => (a.order || 0) - (b.order || 0)));
  const extra = Object.keys(bySection).filter((s) => !SECTION_ORDER.includes(s) && s !== "session");
  const sections = [...SECTION_ORDER.slice(0, 2), ...extra, "settings"].filter((s) => bySection[s] && bySection[s].length);
  const session = bySection.session || [];
  const kicker = { fontSize: 11, letterSpacing: ".22em", fontWeight: 700, color: accent, textTransform: "uppercase", margin: "0 2px 8px" };
  const rowStyle = {
    width: "100%", boxSizing: "border-box", minHeight: 44, padding: "9px 13px", borderRadius: 14, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 12, textAlign: "left",
    background: B.row, border: `1px solid ${B.edge}`, color: B.text,
    fontFamily: MK.sans, fontSize: 15, fontWeight: 600, letterSpacing: ".03em",
  };
  return (
    <>
      {chips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {chips.map((c) => (
            <div key={c.id} title={c.label} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 16,
              background: "rgba(0,0,0,.38)", border: `1px solid ${(c.tint || accent)}66`,
              fontSize: 13, fontWeight: 700, color: B.text, fontFamily: MK.sans,
            }}>
              <span style={{ color: c.tint || accent }}>{c.icon || "◆"}</span>
              <span>{c.value}</span>
              <span style={{ color: B.dim, fontWeight: 500, fontSize: 12 }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {sections.map((s) => (
          <div key={s}>
            <div style={kicker}>{SECTION_LABEL[s] || s}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {bySection[s].map((it) => it.choices ? (
                <div key={it.id} style={{ ...rowStyle, cursor: "default", flexDirection: "column", alignItems: "stretch", gap: 10 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {it.icon && <span aria-hidden="true" style={{ width: 20, textAlign: "center", color: accent }}>{iconNode(it.icon, 18)}</span>}
                    {it.label}
                  </span>
                  <span role="group" aria-label={it.label} style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: it.icon ? 32 : 0 }}>
                    {it.choices.map((c) => (
                      <button key={c.id} type="button" className="ieb-btn" aria-pressed={!!c.active}
                        onClick={() => c.run && c.run()}
                        style={{
                          minHeight: 32, padding: "4px 11px", borderRadius: 16, cursor: "pointer",
                          fontFamily: MK.sans, fontSize: 12.5, fontWeight: 700, letterSpacing: ".06em",
                          background: c.active ? accent : "transparent", color: c.active ? B.ink : B.dim,
                          border: `1px solid ${c.active ? accent : B.edge}`,
                        }}>{c.label}</button>
                    ))}
                  </span>
                </div>
              ) : (
                <button key={it.id} type="button" className="ieb-row" onClick={() => onRun(it.run)} style={rowStyle}>
                  {it.icon && <span aria-hidden="true" style={{ width: 20, textAlign: "center", color: accent }}>{iconNode(it.icon, 18)}</span>}
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {it.badge > 0
                    ? <span style={{ minWidth: 22, height: 22, padding: "0 6px", borderRadius: 11, background: B.star, color: B.ink, fontSize: 12, fontWeight: 800, lineHeight: "22px", textAlign: "center" }}>{it.badge > 99 ? "99+" : it.badge}</span>
                    : <span aria-hidden="true" style={{ color: B.dim }}>›</span>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {foot && session.length > 0 && (
        <div style={{ marginTop: "auto", paddingTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
          {session.map((it) => (
            <button key={it.id} type="button" className="ieb-row" onClick={() => onRun(it.run)}
              style={{ ...rowStyle, justifyContent: "center", background: "rgba(0,0,0,.42)", color: B.dim, fontSize: 13, letterSpacing: ".18em" }}>
              {it.icon && <span aria-hidden="true">{it.icon}</span>}{it.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Aemilia's nook (band foot, or the sheet's top) ──
function Nook({ unseen, onAsk, compact = false }) {
  return (
    <button type="button" className="ieb-btn" aria-label={`Ask Aemilia${unseen ? " (something new)" : ""}`} onClick={onAsk}
      style={{
        width: "100%", height: compact ? 120 : NOOK_H, padding: 0, cursor: "pointer", border: "none", background: "none",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 6,
      }}>
      <div className="ieb-anim" style={{ animation: "iebBob 2.4s ease-in-out infinite alternate", position: "relative" }}>
        <AemiliaSprite size={compact ? 70 : 66} />
        {unseen > 0 && (
          <span aria-hidden="true" style={{
            position: "absolute", top: 0, right: -6, minWidth: 20, height: 20, borderRadius: 10,
            background: B.star, color: B.ink, fontFamily: MK.sans, fontWeight: 800, fontSize: 13,
            lineHeight: "20px", textAlign: "center",
          }}>?</span>
        )}
      </div>
      <span style={{
        padding: "3px 12px", borderRadius: 14, background: "#f3eeff", color: B.ink,
        fontFamily: MK.sans, fontSize: 12.5, fontWeight: 700, letterSpacing: ".04em",
      }}>Ask Aemilia</span>
    </button>
  );
}

// ── The folded pill — Terror's RailMenuPill: drag to move, click to unfold ──
const DRAG_SLOP = 5;
function clampPill(p, w, h) {
  const vw = window.innerWidth, vh = window.innerHeight;
  return { x: Math.min(Math.max(4, p.x), vw - w - 4), y: Math.min(Math.max(4, p.y), vh - h - 4) };
}
function FoldedPill({ v, accent, dot }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!v.pill) return undefined;
    const on = () => {
      const el = ref.current; if (!el) return;
      const p = band.get().pill; if (!p) return;
      const c = clampPill(p, el.offsetWidth, el.offsetHeight);
      if (c.x !== p.x || c.y !== p.y) band.setPill(c);
    };
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [v.pill]);
  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const el = ref.current; if (!el) return;
    const b = el.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: b.left, oy: b.top, moved: false, w: b.width, h: b.height };
    try { el.setPointerCapture(e.pointerId); } catch { /* old browser */ }
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    d.moved = true;
    d.last = clampPill({ x: d.ox + dx, y: d.oy + dy }, d.w, d.h);
    setLive(d.last);
  };
  const onUp = (e) => {
    const d = drag.current; drag.current = null;
    try { ref.current && ref.current.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    if (!d) return;
    if (d.moved) { if (d.last) band.setPill(d.last); setLive(null); }
    else band.expand();
  };
  const at = live || v.pill;
  return (
    <button ref={ref} type="button" className="ieb-btn"
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
      onPointerCancel={() => { drag.current = null; setLive(null); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); band.expand(); } }}
      aria-label="Open the menu band (drag to move)" title="Click to open · drag to move"
      style={{
        position: "fixed", zIndex: Z.pill, ...(at ? { left: at.x, top: at.y } : { right: BAND.RIGHT, top: BAND.MENU_TOP }),
        display: "flex", alignItems: "center", gap: 8, minHeight: 40, padding: "8px 18px", borderRadius: 22,
        background: B.panel, border: `1px solid ${accent}`, color: B.text,
        fontFamily: MK.sans, fontSize: 13, fontWeight: 700, letterSpacing: ".18em",
        boxShadow: `0 0 16px ${accent}40, 0 4px 18px rgba(0,0,0,.45)`,
        cursor: live ? "grabbing" : "grab", touchAction: "none", userSelect: "none", WebkitUserSelect: "none",
      }}>
      <span aria-hidden="true" style={{ fontSize: 15, letterSpacing: 0 }}>☰</span>MENU
      {dot && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: B.star, boxShadow: `0 0 8px ${B.star}` }} />}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
export default function MenuBackdrop({
  title = "",
  accent = "#d4af37",
  pillTouch = { left: 14, bottom: 20 },   // phone / portrait: the MENU pill (thumb reach)
  storageKey = "ie.band.v1",              // per title: fold + pill position
  defaultPanel = "log",                   // the panel the band rests on
  art = null,                             // a painting URL…
  Art = null,                             // …or a component ({ accent }) — e.g. Terror's equipped backdrop
  escKey = true,                          // Esc toggles the MENU panel (a title with its own Esc law passes false)
}) {
  const v = useBand();
  const socialHidden = useSocialHidden();
  const aemMode = useAemiliaMode();
  const aemOpen = useAemiliaOpen();
  const aemUnseen = useAemiliaUnseen();
  const aemHere = aemMode === "backdrop";
  const pillRef = useRef(null);
  const closeRef = useRef(null);
  const sheetRef = useRef(null);
  const [vw, setVw] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));

  // configure, then host — both before paint, so the first frame already wears
  // this title's fold state (layout effects run in order, before the browser paints)
  useLayoutEffect(() => { band.configure({ storageKey, defaultPanel }); }, [storageKey, defaultPanel]);
  const floor = aemHere ? NOOK_H + 22 : BAND.SLOT_BOTTOM;
  useLayoutEffect(() => band.addHost(BAND.SLOT_TOP, floor), [floor]);
  useLayoutEffect(() => band.addFace(), []);
  useEffect(() => { injectMarkChrome(); injectKeys(); }, []);
  useEffect(() => {
    const on = () => setVw(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const menuOpen = v.panel === "menu" && !v.collapsed;
  // A title modal up (ready-up, a door menu, a Mark): the band goes quiet —
  // painting only, no buttons, no panels — so nothing sits over the modal.
  // (Aemilia's own sheet raises it too, but she sits BESIDE the band — it stays.)
  const quiet = socialHidden && !aemOpen;
  useEffect(() => { band.setQuiet(quiet); }, [quiet]);
  useEffect(() => () => band.setQuiet(false), []);

  // Esc: the pause-menu key. Never while typing, over a title modal, or over Aemilia's sheet.
  useEffect(() => {
    if (!escKey) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (aemOpen) return;                              // her Esc closes HER
      if (menuOpen) { closeBackdrop(); return; }
      if (!v.live || socialHidden) return;
      openBackdrop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [escKey, menuOpen, v.live, socialHidden, aemOpen]);

  // The phone sheet: a title modal rising closes it (it would sit on top).
  useEffect(() => { if (v.sheet && menuOpen && socialHidden) closeBackdrop(); }, [v.sheet, menuOpen, socialHidden]);
  useEffect(() => {
    if (v.sheet && menuOpen) { const t = setTimeout(() => closeRef.current && closeRef.current.focus(), 30); return () => clearTimeout(t); }
    return undefined;
  }, [v.sheet, menuOpen]);

  // Her sheet opened from the nook: focus comes home to the MENU pill when it closes.
  const aemWasOpen = useRef(false);
  useEffect(() => {
    if (aemWasOpen.current && !aemOpen && aemHere) {
      const t = setTimeout(() => pillRef.current && pillRef.current.focus(), 40);
      aemWasOpen.current = false;
      return () => clearTimeout(t);
    }
    aemWasOpen.current = aemOpen;
    return undefined;
  }, [aemOpen, aemHere]);

  // A row closes the MENU panel (band: back to the resting panel), then runs.
  const runRow = useCallback((fn) => {
    if (band.get().panel === "menu") band.open(defaultPanel || null);
    if (typeof fn === "function") setTimeout(fn, 0);
  }, [defaultPanel]);
  const ask = useCallback(() => { if (band.get().panel === "menu" && !band.get().desktop) closeBackdrop(); openAemilia(); }, []);

  if (!v.live) return null;

  const badges = v.rows.reduce((n, it) => n + (it.badge || 0), 0)
    + v.tabList.reduce((n, t) => n + (t.badge || 0), 0) + ((aemHere || aemMode === "button") ? aemUnseen : 0);

  // ══ PHONE / PORTRAIT: pill + sheet ══════════════════════════════════
  if (v.sheet) {
    const anchor = pillTouch;
    if (!menuOpen) {
      if (socialHidden || aemOpen) return null;
      return (
        <button ref={pillRef} type="button" className="ieb-btn" aria-label={`Open menu${badges ? " (something new)" : ""}`}
          onClick={openBackdrop}
          style={{
            position: "fixed", zIndex: 170, ...anchor,
            display: "flex", alignItems: "center", gap: 8, minHeight: 44, padding: "8px 16px", borderRadius: 22, cursor: "pointer",
            background: B.panel, border: `1px solid ${accent}`, color: B.text,
            fontFamily: MK.sans, fontSize: 13, fontWeight: 700, letterSpacing: ".18em",
            boxShadow: `0 0 16px ${accent}40, 0 4px 18px rgba(0,0,0,.45)`,
          }}>
          <span aria-hidden="true" style={{ fontSize: 15, letterSpacing: 0 }}>☰</span>MENU
          {badges > 0 && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: B.star }} />}
        </button>
      );
    }
    // on a phone the tabs are rows: each opens its owner's own phone panel
    const tabRows = v.tabList.filter((t) => t.run).map((t) => ({ ...t, section: "people" }));
    return (
      <>
        <div onClick={closeBackdrop} aria-hidden="true" className="ieb-anim"
          style={{ position: "fixed", inset: 0, zIndex: Z.sheet - 2, background: "rgba(4,2,14,0.34)", animation: "iebFade .2s ease-out" }} />
        <div ref={sheetRef} role="dialog" aria-modal="true" aria-label="Menu" className="ieb-anim"
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); closeBackdrop(); }
            if (e.key === "Tab" && sheetRef.current) {
              const f = [...sheetRef.current.querySelectorAll("button")];
              if (f.length) {
                const first = f[0], last = f[f.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
              }
            }
            e.stopPropagation();
          }}
          onKeyUp={(e) => e.stopPropagation()}
          style={{
            "--ieb-accent": accent, position: "fixed", zIndex: Z.sheet, top: 0, right: 0, bottom: 0,
            width: "min(340px, 92vw)", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden",
            background: B.panel, borderLeft: `1px solid ${accent}66`, color: B.text,
            boxShadow: "-18px 0 50px rgba(0,0,0,.55)", fontFamily: MK.sans, animation: "iebIn .24s ease-out",
          }}>
          {Art ? <Art accent={accent} /> : art
            ? <img src={art} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
            : <BackdropArt accent={accent} />}
          <div style={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "16px 16px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0, fontFamily: MK.display, fontSize: 15, letterSpacing: ".34em", color: accent, textShadow: `0 0 18px ${accent}66, 0 2px 8px #000`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title ? title.toUpperCase() : "MENU"}</div>
              <button ref={closeRef} type="button" className="ieb-btn" aria-label="Close menu" onClick={closeBackdrop}
                style={{ flex: "0 0 auto", width: 40, height: 40, borderRadius: 20, border: `1px solid ${B.edge}`, background: "rgba(0,0,0,.3)", color: B.text, cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
            {aemHere && <Nook unseen={aemUnseen} onAsk={ask} compact />}
            <RowList rows={[...v.rows, ...tabRows]} chips={v.chipList} accent={accent} onRun={(fn) => { closeBackdrop(); if (typeof fn === "function") setTimeout(fn, 0); }} />
          </div>
        </div>
      </>
    );
  }

  // ══ DESKTOP: the band ═══════════════════════════════════════════════
  if (v.collapsed) return quiet ? null : <FoldedPill v={v} accent={accent} dot={badges > 0} />;

  const bw = bandWidthPx();
  void vw;   // re-render on resize so bw follows the window
  const box = { position: "fixed", right: BAND.RIGHT, width: BAND.W };
  const tabBtn = (lit) => ({
    width: BAND.ROW_H - 4, height: BAND.ROW_H - 4, borderRadius: "50%", cursor: "pointer", position: "relative",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
    background: lit ? accent : B.glass, color: lit ? B.ink : B.text,
    border: `1px solid ${lit ? accent : B.edge}`, boxShadow: "0 4px 14px rgba(0,0,0,.5)",
  });

  return (
    <div style={{ "--ieb-accent": accent }}>
      {/* the painting — the band's ground, feathered on the edge that faces the game */}
      <div aria-hidden="true" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: bw, zIndex: Z.art, pointerEvents: "none", overflow: "hidden",
        WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 22px)", maskImage: "linear-gradient(to right, transparent 0, #000 22px)",
      }}>
        {Art ? <Art accent={accent} /> : art
          ? <img src={art} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <BackdropArt accent={accent} />}
      </div>
      {!quiet && <>

      {/* ☰ MENU — the front door: opens the MENU panel in the band */}
      <button ref={pillRef} type="button" className="ieb-btn" aria-pressed={menuOpen}
        aria-label={`Menu${badges ? " (something new)" : ""}`} onClick={() => band.toggle("menu")}
        style={{
          ...box, top: BAND.MENU_TOP, zIndex: Z.chrome, boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 38, padding: "7px 16px",
          borderRadius: 22, cursor: "pointer",
          background: menuOpen ? accent : B.panel, color: menuOpen ? B.ink : B.text, border: `1px solid ${accent}`,
          fontFamily: MK.sans, fontSize: 12.5, fontWeight: 700, letterSpacing: ".2em",
          boxShadow: `0 0 14px ${accent}44`,
        }}>
        <span aria-hidden="true" style={{ fontSize: 14, letterSpacing: 0 }}>☰</span>{title ? title.toUpperCase() : "MENU"}
      </button>

      {/* the tab row: fold on the left, the band's panels on the right */}
      <div style={{ ...box, top: BAND.ROW_TOP, height: BAND.ROW_H, zIndex: Z.chrome, display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" className="ieb-btn" aria-label="Minimize the band" title="Fold everything into the Menu pill"
          onClick={() => band.collapse()} style={{ ...tabBtn(false), width: 32, height: 32, fontWeight: 900, color: accent }}>—</button>
        <span style={{ flex: 1 }} />
        {/* "Just a button": Aemilia sits up here with the others (caP'n, 2026-10-21) */}
        {aemMode === "button" && !aemOpen && (
          <button type="button" className="ieb-btn" title="Ask Aemilia"
            aria-label={`Ask Aemilia${aemUnseen ? " (something new)" : ""}`} onClick={ask}
            style={{ ...tabBtn(false), overflow: "visible" }}>
            <span aria-hidden="true" style={{ display: "flex", marginTop: -2 }}><AemiliaSprite size={30} /></span>
            {aemUnseen > 0 && (
              <span aria-hidden="true" style={{ position: "absolute", top: -3, right: -3, minWidth: 16, height: 16, borderRadius: 8, background: B.star, color: B.ink, fontSize: 11, fontWeight: 800, lineHeight: "16px", textAlign: "center", fontFamily: MK.sans }}>?</span>
            )}
          </button>
        )}
        {v.tabList.map((t) => {
          const lit = v.panel === t.id;
          return (
            <button key={t.id} type="button" className="ieb-btn" aria-pressed={lit} title={t.label}
              aria-label={`${t.label}${t.badge ? ` (${t.badge} new)` : ""}`}
              onClick={() => band.toggle(t.id)} style={tabBtn(lit)}>
              <span aria-hidden="true" style={{ display: "flex" }}>{iconNode(t.icon, 22) || "•"}</span>
              {t.badge > 0 && !lit && (
                <span aria-hidden="true" style={{ position: "absolute", top: -3, right: -3, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: B.star, color: B.ink, fontSize: 10, fontWeight: 800, lineHeight: "16px", textAlign: "center", fontFamily: MK.sans }}>{t.badge > 99 ? "99+" : t.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* the MENU panel — the rows */}
      {menuOpen && (
        <div role="region" aria-label="Menu" style={{
          ...box, top: v.slotTop, bottom: floor, zIndex: Z.panel, boxSizing: "border-box",
          display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", padding: 12,
          background: B.glass, border: `1px solid ${B.edge}`, borderRadius: 16, backdropFilter: "blur(4px)",
          animation: "iebFade .18s ease-out",
        }}>
          <RowList rows={v.rows} chips={v.chipList} accent={accent} onRun={runRow} />
        </div>
      )}

      {/* Aemilia's house */}
      {aemHere && !aemOpen && (
        <div style={{ ...box, bottom: 14, height: NOOK_H, zIndex: Z.chrome }}>
          <Nook unseen={aemUnseen} onAsk={ask} />
        </div>
      )}
      </>}
    </div>
  );
}

// For a tab owner drawing its body into the band: portal to document.body, so
// the body paints above the band's painting (the title's root can't).
export function BandPortal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}
